/**
 * previewParams.js
 *
 * Stores and retrieves CraftCMS live-preview URL params in sessionStorage so
 * they survive SPA navigation within the preview iframe.
 *
 * Flow:
 *  1. CraftCMS loads:  /previews/manual/{id}/site/{siteId}
 *                        ?token=...&api-url=...&web=1&x-craft-live-preview=...
 *  2. getPreviewParams() reads from the URL and caches to sessionStorage.
 *  3. User clicks Home / Switch Manual — URL may not carry the params.
 *  4. getPreviewParams() falls back to sessionStorage so the Apollo client
 *     still receives the correct token and API endpoint.
 *
 * Outside the preview iframe (no params ever set) sessionStorage stays empty
 * and everything returns {} / "".
 */

const STORAGE_KEY = 'craftPreviewParams';

/** Params we want to preserve across preview navigation */
export const PREVIEW_PARAM_KEYS = [
  'token',
  'x-craft-live-preview',
  'x-craft-preview',
  'api-url',
  'web',
];

/**
 * Read preview params, preferring the current URL (and refreshing
 * sessionStorage when URL params are found), then falling back to
 * sessionStorage for pages navigated to without the params in the URL.
 *
 * @returns {Object} key→value map of present preview params
 */
export function getPreviewParams() {
  if (typeof window === 'undefined') return {};

  const src = new URLSearchParams(window.location.search);
  const fromUrl = {};
  PREVIEW_PARAM_KEYS.forEach(k => {
    if (src.has(k)) fromUrl[k] = src.get(k);
  });

  if (Object.keys(fromUrl).length > 0) {
    // Refresh storage whenever URL has params
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl)); } catch { /* private browsing */ }
    return fromUrl;
  }

  // Nothing in URL — try sessionStorage
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Build a query string from current or stored preview params.
 * Returns "" when no preview context is active (normal Gatsby navigation).
 *
 * @returns {string}  e.g. "?token=abc&api-url=...&web=1"  or  ""
 */
export function getPreviewQueryString() {
  const params = getPreviewParams();
  const out = new URLSearchParams();
  PREVIEW_PARAM_KEYS.forEach(k => { if (params[k]) out.set(k, params[k]); });
  const str = out.toString();
  return str ? `?${str}` : '';
}

/**
 * True when the current page is running inside an iframe.
 * Cross-origin iframes throw on window.top access — the catch handles that.
 */
export function isInIframe() {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    return true; // cross-origin iframe — must be embedded
  }
}

/**
 * True when there is an active preview session (token or live-preview flag
 * present in the URL or sessionStorage).
 */
export function isInPreviewMode() {
  const params = getPreviewParams();
  return !!(params['token'] || params['x-craft-live-preview']);
}

/**
 * Clear the stored preview session. Call when the user explicitly exits
 * preview mode so stale params don't persist for the rest of the tab session.
 */
export function clearPreviewSession() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* private browsing */ }
}

const PARKED_STORAGE_KEY = 'craftPreviewParams_parked';

/**
 * Park the preview session: moves active params to a parked key so
 * the Apollo client loses the token (pausing live updates) while
 * keeping them available for restore.
 */
export function parkPreviewSession() {
  try {
    const active = sessionStorage.getItem(STORAGE_KEY);
    if (active) {
      sessionStorage.setItem(PARKED_STORAGE_KEY, active);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch { /* private browsing */ }
}

/**
 * Restore a previously parked preview session back to the active key.
 */
export function restorePreviewSession() {
  try {
    const parked = sessionStorage.getItem(PARKED_STORAGE_KEY);
    if (parked) {
      sessionStorage.setItem(STORAGE_KEY, parked);
      sessionStorage.removeItem(PARKED_STORAGE_KEY);
    }
  } catch { /* private browsing */ }
}

/**
 * True when there is a parked (paused) preview session.
 */
export function isPreviewParked() {
  try {
    return !!sessionStorage.getItem(PARKED_STORAGE_KEY);
  } catch {
    return false;
  }
}
